import { useEffect, useState } from "react";
import {
  AVPLAY_ELEMENT_ID,
  hideAvPlay,
  startAvPlay,
  type AvPlayDrm,
} from "./avplay";

/**
 * The React half of the AVPlay path (TIZEN-16).
 *
 * Two components, because AVPlay separates the two things a `<video>` conflates:
 * the SURFACE is a long-lived hole punched through to a hardware plane and must
 * outlive every clip, while the PLAYER is per-source and torn down between them.
 * Mounting the surface with the clip - the obvious shape - rebuilds the plane on
 * every change, which is the black gap this exists to avoid.
 */

/**
 * The native surface. Mount ONCE, high in the tree, and leave it there.
 *
 * `visibility: hidden` rather than unmounted when nothing is playing: see
 * `showAvPlay`. The z-index puts it above the page because on these panels the
 * element is a hole punch rather than a painted layer - HTML drawn over it
 * still composites on top, so a diagnostics caption is not hidden by it.
 */
export function AvPlaySurface() {
  return (
    <object
      id={AVPLAY_ELEMENT_ID}
      type="application/avplayer"
      data-testid="avplay-surface"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        visibility: "hidden",
        zIndex: 100,
      }}
    />
  );
}

export interface AvPlayLayerProps {
  /** What to play. Already resolved to a local copy where one exists. */
  src: string;
  /** What follows it, warmed during the gap. */
  nextSrc?: string;
  muted: boolean;
  /**
   * Loop this source rather than advancing. True for a solo playlist, where
   * the playhead resolver keeps returning the same item and nothing else will
   * ever restart it.
   */
  loop: boolean;
  isDash?: boolean;
  drm?: AvPlayDrm;
  /** This source cannot be played; the surface should skip it. */
  onFailed: () => void;
  /** Stall text for the diagnostics caption and the heartbeat. Null when healthy. */
  onMedia: (state: string | null) => void;
}

export default function AvPlayLayer({
  src,
  nextSrc,
  muted,
  loop,
  isDash = false,
  drm,
  onFailed,
  onMedia,
}: AvPlayLayerProps) {
  /**
   * Bumped when the stream completes, to start it again.
   *
   * The firmware's own `setLooping` is asked for first and is absent on several
   * builds, so this is the fallback that actually holds a wall on a one-clip
   * playlist. Re-running the effect re-opens the source, which on AVPlay is the
   * supported way to restart - unlike the HTML element, where a seek to zero is
   * what wedges these panels.
   */
  const [pass, setPass] = useState(0);

  useEffect(() => {
    if (!src) return;
    const stop = startAvPlay({
      src,
      nextSrc,
      muted,
      loop,
      isDash,
      drm,
      screen: {
        width: typeof window === "undefined" ? 1920 : window.innerWidth,
        height: typeof window === "undefined" ? 1080 : window.innerHeight,
      },
      onEnded: () => {
        if (loop) setPass((p) => p + 1);
      },
      onError: (msgText, fatal) => {
        console.warn(`[avplay] ${fatal ? "FATAL" : "stall"}: ${msgText}`);
        onMedia(msgText);
        // Only a source that will not play at all is struck out. A stall is
        // reported and left alone: `dead` is permanent for the session, so
        // treating every stall as a dead item would let a bad ten minutes of
        // network empty the playlist and blank the wall until a reboot.
        if (fatal) onFailed();
      },
      onEvent: (msgText) => {
        console.warn(msgText);
        // A stall WARNING is worth surfacing while it is still happening; a
        // plain "playing" line clears the caption. Anything else is progress
        // noise that belongs in the log and not on an operator's screen.
        if (msgText.includes("stall")) onMedia(msgText);
        else onMedia(null);
      },
    });
    return stop;
    // `onMedia`/`onFailed` are intentionally absent: they are recreated on every
    // render of the surface, and including them would stop and re-open the
    // stream once a second for the whole life of the clip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, nextSrc, muted, loop, isDash, drm?.licenceServerUrl, pass]);

  // Nothing rendered: the picture is on a hardware plane behind this document,
  // reached through the surface above. Hiding it on the way out is the only
  // visual act this component has.
  useEffect(() => () => hideAvPlay(), []);
  return null;
}
